import React, { useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Space,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  useCreatePaymentMutation,
  useGetRouteObligationsForPaymentQuery,
  RouteObligationForPayment,
} from '../../store/api/financeApi';
import { formatCurrency } from '../../utils/dateUtils';
import { obligationStageLabel, obligationStageToPaymentType } from '../../utils/obligationStageUi';
import { financeDirectionLabel } from '../../utils/enumUiLabels';

const { Option } = Select;

function formatObligationLabel(obl: RouteObligationForPayment): string {
  const stage = obligationStageLabel(obl.stage);
  const direction = financeDirectionLabel(obl.direction);
  const amount = formatCurrency(obl.remainingAmount ?? obl.amountWithVAT);
  const dueStr = obl.dueDate ? dayjs(obl.dueDate).format('DD.MM') : '';
  return `${stage} ${direction} — ${amount} (срок ${dueStr})`;
}

interface CreatePaymentModalProps {
  visible: boolean;
  routeId: number | null;
  routeApplicationId?: number | null;
  initialObligationId?: number | null;
  /** Обязательства заявки (INCOME без маршрута) — тогда список не грузится по routeId */
  preloadedObligations?: RouteObligationForPayment[] | null;
  /** Если задано — в списке только обязательства этого направления; иначе показывается выбор направления */
  obligationDirectionFilter?: 'INCOME' | 'EXPENSE' | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreatePaymentModal: React.FC<CreatePaymentModalProps> = ({
  visible,
  routeId,
  routeApplicationId,
  initialObligationId,
  preloadedObligations = null,
  obligationDirectionFilter = null,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const listDirection = Form.useWatch('paymentListDirection', form);
  const effectiveDirection: 'INCOME' | 'EXPENSE' | undefined =
    obligationDirectionFilter ||
    (listDirection === 'INCOME' || listDirection === 'EXPENSE' ? listDirection : undefined);

  const [createPayment, { isLoading }] = useCreatePaymentMutation();

  const usePreloaded = Boolean(preloadedObligations && preloadedObligations.length > 0);
  const effectiveRouteId = routeId != null && routeId > 0 ? routeId : 0;

  const { data: obligationsFromRoute = [], isLoading: loadingObligationsFromRoute } =
    useGetRouteObligationsForPaymentQuery(effectiveRouteId, {
      skip: !visible || !effectiveRouteId || usePreloaded,
    });

  const obligations = useMemo(() => {
    if (usePreloaded && preloadedObligations) return preloadedObligations;
    return obligationsFromRoute;
  }, [usePreloaded, preloadedObligations, obligationsFromRoute]);

  const loadingObligations = usePreloaded ? false : loadingObligationsFromRoute;

  const filteredObligations = useMemo(() => {
    if (!effectiveDirection) return [];
    return obligations.filter((o) => o.direction === effectiveDirection);
  }, [obligations, effectiveDirection]);

  const canPickObligation = Boolean(effectiveRouteId) || usePreloaded;

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ routeId: routeId ?? undefined });
      if (obligationDirectionFilter) {
        form.setFieldsValue({ paymentListDirection: obligationDirectionFilter });
      }
    }
  }, [visible, routeId, form, obligationDirectionFilter]);

  useEffect(() => {
    if (!visible || !initialObligationId || filteredObligations.length === 0) {
      return;
    }
    const obl = filteredObligations.find((o) => o.id === initialObligationId);
    if (obl) {
      form.setFieldsValue({
        obligationId: obl.id,
        amount: obl.remainingAmount ?? obl.amountWithVAT,
        paymentType: obligationStageToPaymentType(obl.stage),
      });
    }
  }, [visible, initialObligationId, filteredObligations, form]);

  useEffect(() => {
    if (filteredObligations.length === 1 && visible && effectiveDirection) {
      const obl = filteredObligations[0];
      form.setFieldsValue({
        obligationId: obl.id,
        amount: obl.remainingAmount ?? obl.amountWithVAT,
      });
    }
  }, [filteredObligations, visible, effectiveDirection, form]);

  const handleFinish = async (values: any) => {
    try {
      if (!values.obligationId) {
        message.error('Выберите обязательство');
        return;
      }
      const obl = filteredObligations.find((o) => o.id === values.obligationId);
      if (!obl) {
        message.error('Обязательство не найдено');
        return;
      }

      const payload: any = {
        amount: values.amount,
        paymentDate: values.paymentDate?.toISOString?.(),
        obligationId: values.obligationId,
        routeId:
          effectiveRouteId > 0 ? effectiveRouteId : obl?.routeId != null && obl.routeId > 0 ? obl.routeId : null,
        applicationId: obl?.applicationId ?? routeApplicationId,
        paymentType: obligationStageToPaymentType(obl?.stage),
      };

      await createPayment(payload).unwrap();
      message.success('Платеж успешно создан');
      onClose();
      onSuccess?.();
    } catch (err: any) {
      const msg = err?.data?.error || err?.message || 'Ошибка при создании платежа';
      message.error(msg);
    }
  };

  const showDirectionPicker = !obligationDirectionFilter;

  return (
    <Modal title="Создать платеж" open={visible} onCancel={onClose} footer={null} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {canPickObligation && effectiveDirection ? (
          <>
            {showDirectionPicker && effectiveRouteId > 0 && (
              <Form.Item
                name="paymentListDirection"
                label="Направление"
                rules={[{ required: true, message: 'Выберите направление платежа' }]}
              >
                <Select placeholder="Доход или расход">
                  <Option value="INCOME">Получить от клиента (доход)</Option>
                  <Option value="EXPENSE">Оплатить перевозчику (расход)</Option>
                </Select>
              </Form.Item>
            )}
            {filteredObligations.length > 0 && (
              <Form.Item
                name="obligationId"
                label="Обязательство"
                rules={[{ required: true, message: 'Выберите обязательство' }]}
              >
                <Select
                  placeholder="Выберите обязательство"
                  loading={loadingObligations}
                  optionFilterProp="label"
                  onChange={(val) => {
                    const obl = filteredObligations.find((o) => o.id === val);
                    if (obl) {
                      form.setFieldsValue({
                        amount: obl.remainingAmount ?? obl.amountWithVAT,
                        paymentType: obligationStageToPaymentType(obl.stage),
                      });
                    }
                  }}
                >
                  {filteredObligations.map((obl) => (
                    <Option key={obl.id} value={obl.id} label={formatObligationLabel(obl)}>
                      {formatObligationLabel(obl)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {!loadingObligations && filteredObligations.length === 0 && (
              <div style={{ marginBottom: 12, color: '#999', fontSize: 12 }}>
                Нет открытых обязательств с остатком для выбранного направления.
              </div>
            )}
          </>
        ) : null}

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.obligationId !== curr.obligationId}>
          {({ getFieldValue }) => {
            const oblId = getFieldValue('obligationId');
            const obl = oblId ? filteredObligations.find((o) => o.id === oblId) : null;
            const remaining = obl ? obl.remainingAmount ?? obl.amountWithVAT : 0;

            return (
              <>
                <Form.Item
                  name="amount"
                  label={remaining > 0 ? `Сумма (остаток по обязательству: ${formatCurrency(remaining)})` : 'Сумма'}
                  rules={[{ required: true, message: 'Введите сумму' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={remaining || undefined}
                    formatter={(v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                    parser={(v) => (v ? Number(String(v).replace(/\$\s?|(,*)/g, '')) : 0)}
                    placeholder="Введите сумму"
                  />
                </Form.Item>
              </>
            );
          }}
        </Form.Item>

        <Form.Item name="paymentDate" label="Дата платежа">
          <DatePicker style={{ width: '100%' }} placeholder="Выберите дату" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              disabled={!effectiveDirection || filteredObligations.length === 0}
            >
              Создать
            </Button>
            <Button onClick={onClose}>Отмена</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreatePaymentModal;
